import { useState } from 'react';
import type { FC } from 'react';
import { Box, IconButton, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRouter } from '@tanstack/react-router';
import { showGlobalError } from '@/components/feedback';

import { MobileButton, MobileInput, Text } from '@/components/ui';
import { useAuth } from '@/features/auth/hooks';
import { loginIcon, passwordIcon } from '@/assets';

// Custom SVG Eye Icons
const EyeIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z' />
  </svg>
);

const EyeOffIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z' />
  </svg>
);

interface Props {
  hideTitle?: boolean;
}

export const LoginForm: FC<Props> = ({ hideTitle }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { login, isLoading, error, clearError } = useAuth();

  const [isError, setIsError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authInfo, setAuthInfo] = useState({
    userName: '',
    password: '',
    rememberMe: true,
  });

  const handleChangeUserName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAuthInfo(prev => ({ ...prev, userName: event.target.value }));
    if (isError) setIsError(false);
    if (error) clearError();
  };

  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAuthInfo(prev => ({ ...prev, password: event.target.value }));
    if (isError) setIsError(false);
    if (error) clearError();
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const onClickSubmit = async () => {
    if (!authInfo.userName || !authInfo.password) {
      setIsError(true);
      return;
    }

    try {
      const result = await login(authInfo);

      if (result.success) {
        router.navigate({ to: '/projects' });
      } else {
        showGlobalError(result.error || 'Login failed');
      }
    } catch (err) {
      console.error('Login failed:', err);
      showGlobalError('Login failed');
    }
  };

  return (
    <Box component='form' autoComplete='off' sx={{ width: '35.8125rem' }}>
      {!hideTitle && (
        <Text
          sx={{
            color: 'primary.main',
            fontSize: '2rem',
            lineHeight: '3rem',
            mb: '0.625rem',
          }}
        >
          {t('login_title')}
        </Text>
      )}
      <MobileInput
        label={t('login_label_login')}
        name='login'
        error={isError && !authInfo.userName}
        helperText={''}
        value={authInfo.userName}
        placeholder={`${t('login_username')}`}
        onChange={handleChangeUserName}
        startAdornment={
          <InputAdornment position='start'>
            <Box component='img' src={loginIcon} />
          </InputAdornment>
        }
      />
      <MobileInput
        label={t('password_label_login')}
        name='password'
        error={isError && !authInfo.password}
        helperText={''}
        value={authInfo.password}
        placeholder={`${t('login_password')}`}
        onChange={handleChangePassword}
        type={showPassword ? 'text' : 'password'}
        startAdornment={
          <InputAdornment position='start'>
            <Box component='img' src={passwordIcon} />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position='end'>
            <IconButton
              onClick={handleTogglePasswordVisibility}
              edge='end'
              aria-label={
                showPassword ? t('hide_password') : t('show_password')
              }
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </IconButton>
          </InputAdornment>
        }
      />
      <MobileButton
        onClick={onClickSubmit}
        loading={isLoading}
        fullWidth
        btnColor='#1C5B9C'
        sx={{
          height: '3rem',
          fontSize: '1.5rem',
          mt: '1.25rem',
        }}
      >
        {t('login_button')}
      </MobileButton>
    </Box>
  );
};
